import { getScrollPosition } from "../utils/scroll-position";
import { supportsPassiveEvents } from "../utils/feature-detects";

const UP = "up";
const DOWN = "down";

let height = 0;
let header = document.querySelector(".js-header");
let fauxHeader = document.createElement("div");
let eventOptions = supportsPassiveEvents() ? { passive: true } : false;

let classes = {
	pinned: "is-pinned",
	unpinned: "is-unpinned",
	detached: "is-detached",
	animated: "is-animated",
};

let lastScrollY = 0;
let scrollOffset = 0;
let tolerance = { up: 10, down: 10 };
let headerOffset = { top: 0, bottom: 0 };

let isDetached = false;
let isFloating = false; // Header's default styling is absolute, i.e. floats on the page, we don't show the faux header to offset in this scenario.
let isPinned = false;
let isSticky = false;

let resizing = false;

export function init() {
	if (!header) {
		return;
	}

	isSticky = header.dataset.sticky === "1";
	header.parentNode.insertBefore(fauxHeader, header);

	/**
	 * Defer event registration to handle browser potentially restoring previous
	 * scroll position.
	 */
	setTimeout(function init() {
		calcLayout();
		updateScroll();

		window.addEventListener("resize", onResize, eventOptions);
		document.addEventListener("scroll", onScroll, eventOptions);
	});
}

export function calcLayout() {
	let { y: scrollY } = getScrollPosition();

	let headerRect = header.getBoundingClientRect();
	let headerStyle = window.getComputedStyle(header);
	let fauxHeaderRect = fauxHeader.getBoundingClientRect();
	let headerPosition = headerStyle.getPropertyValue("position");

	isFloating = ["absolute", "fixed"].includes(headerPosition);

	height = headerRect.height;
	headerOffset.top = fauxHeaderRect.top + scrollY;
	headerOffset.bottom = fauxHeaderRect.top + height + scrollY;

	let element = document.querySelector(".js-header-offset");

	if (!element) {
		scrollOffset = headerOffset.bottom;
	} else {
		let rect = element.getBoundingClientRect();
		scrollOffset = rect.bottom + scrollY;
	}

	setFauxHeader();
	resizing = false;
}

export function freeze() {
	if (!isSticky) {
		return;
	}

	document.removeEventListener("scroll", updateScroll, eventOptions);
}

export function unfreeze() {
	if (!isSticky) {
		return;
	}

	document.addEventListener("scroll", updateScroll, eventOptions);
}

function updateScroll() {
	let { y: scrollY } = getScrollPosition();
	let distance = Math.abs(scrollY - lastScrollY);
	let direction = scrollY < lastScrollY ? UP : DOWN;
	let toleranceExceeded = distance > tolerance[direction];

	lastScrollY = scrollY;

	if (
		(isDetached && !isPinned && scrollY <= headerOffset.bottom) ||
		scrollY <= headerOffset.top
	) {
		return reset();
	}

	if (!isDetached && scrollY >= scrollOffset) {
		unpin();
		detach();

		return;
	}

	if (!isDetached || !toleranceExceeded) {
		return;
	}

	if (direction === UP && !isPinned) {
		pin();
	} else if (direction === DOWN && isPinned) {
		unpin();
	}
}

function reset() {
	attach();

	isPinned = false;
	header.classList.remove(classes.pinned);
	header.classList.remove(classes.unpinned);
	header.classList.remove(classes.animated);
}

function setFauxHeader() {
	if (isFloating || !isDetached) {
		fauxHeader.style.height = "";
	} else {
		fauxHeader.style.height = `${height}px`;
	}
}

function detach() {
	isDetached = true;
	header.classList.add(classes.detached);

	setFauxHeader();
}

function attach() {
	isDetached = false;
	header.classList.remove(classes.detached);

	setFauxHeader();
}

function pin() {
	isPinned = true;
	header.classList.add(classes.pinned);
	header.classList.remove(classes.unpinned);
	header.classList.add(classes.animated);
}

function unpin() {
	isPinned = false;
	header.classList.add(classes.unpinned);
	header.classList.remove(classes.pinned);
}

function onScroll() {
	if (resizing) {
		return;
	}

	updateScroll();
}

function onResize() {
	if (resizing) {
		return;
	}

	resizing = true;
	requestAnimationFrame(calcLayout);
}
